<template>
  <div :class="['rx', isPhone ? 'rx--phone' : 'rx--desktop']">
    <!-- PHONE -->
    <PhoneFolderView
      v-if="isPhone"
      :current-node="currentNode"
      :path-nodes="pathNodes"
      :items="phoneItems"
      :loading="currentLoading"
      :sort-key="sortKey"
      :has-fab="showFab || selectMode"
      :select-mode="selectMode"
      :selected="selected"
      @actions="openActionSheet"
      @back="goToParent"
      @open-jump="openJump"
      @toggle-sort="toggleSort"
      @overflow="openOverflowSheet"
      @select-start="enterSelectMode"
      @toggle-select="toggleSelect"
      @select-cancel="exitSelectMode"
      @open="openItem" />

    <!-- DESKTOP -->
    <div v-else class="rx-desk">
      <div class="rx-desk__header">
        <h1 class="rx-desk__title">Resources</h1>
        <div class="rx-desk__search">
          <i class="fas fa-search rx-desk__search-icon" aria-hidden="true"></i>
          <input
            v-model="desktopQuery"
            type="search"
            class="rx-desk__input"
            placeholder="Search all folders and files..."
            aria-label="Search all folders and files" />
        </div>
        <div class="rx-desk__actions">
          <button type="button" class="rx-btn rx-btn--outline" disabled title="Coming soon"><i class="fas fa-upload"></i> Bulk upload</button>
          <button
            type="button"
            class="rx-btn rx-btn--solid"
            :disabled="!canCreateHere || actionPending"
            :title="canCreateHere ? 'New folder in ' + currentNode.name : 'Folders cannot be created here'"
            @click="openDeskCreate"><i class="fas fa-plus"></i> New folder</button>
        </div>
      </div>

      <div class="rx-desk__main">
        <aside class="rx-card rx-desk__tree">
          <div class="rx-desk__tree-head">
            <span class="rx-label">Folders</span>
            <button type="button" class="rx-link" @click="collapseAll">Collapse all</button>
          </div>
          <div class="rx-desk__tree-body">
            <FolderTree
              ref="deskTree"
              variant="desktop"
              :nodes="nodes"
              :current-id="currentId"
              :expanded="desktopExpanded"
              :collapse-limit="2"
              :show-all="showAllProps"
              :query="desktopQuery"
              :can-drop="uploadTargetOk"
              @toggle="onTreeToggle"
              @navigate="navigateTo"
              @show-all="onShowAll"
              @drop-files="onTreeDrop" />
          </div>
          <div class="rx-desk__tree-foot">Drag files or folders onto any folder in this tree to upload straight into it.</div>
        </aside>

        <section
          :class="['rx-card', 'rx-desk__content', contentDragOver && canUploadHere ? 'is-drop-target' : '']"
          @dragover="onContentDragOver"
          @dragleave="onContentDragLeave"
          @drop="onContentDrop">
          <div v-if="contentDragOver" :class="['rx-drop', canUploadHere ? '' : 'rx-drop--blocked']" aria-hidden="true">
            <div class="rx-drop__box">
              <template v-if="canUploadHere">
                <i class="fas fa-cloud-upload-alt rx-drop__icon"></i>
                <div class="rx-drop__title">Drop files or a whole folder here</div>
                <div class="rx-drop__sub">Subfolders are recreated automatically. Uploads go into <b>{{ currentNode.name }}</b>.</div>
              </template>
              <template v-else>
                <i class="fas fa-ban rx-drop__icon"></i>
                <div class="rx-drop__title">{{ uploadHereReason }}</div>
              </template>
            </div>
          </div>
          <div class="rx-desk__content-head">
            <nav class="rx-crumbs" aria-label="Breadcrumb">
              <template v-for="(n, i) in pathNodes">
                <span v-if="i > 0" :key="'sep' + n.id" class="rx-crumbs__sep">/</span>
                <b v-if="i === pathNodes.length - 1" :key="n.id" class="rx-crumbs__current">{{ n.name }}</b>
                <a v-else :key="n.id" href="#" class="rx-crumbs__link" @click.prevent="navigateTo(n)">{{ n.name }}</a>
              </template>
            </nav>
            <div class="rx-desk__content-actions">
              <label class="rx-check"><input type="checkbox" :checked="showArchived" :disabled="actionPending" @change="setShowArchived($event.target.checked)" /> Show archived</label>
              <button
                type="button"
                class="rx-btn rx-btn--outline-plain"
                :disabled="!canUploadHere"
                :title="canUploadHere ? 'Upload files into ' + currentNode.name : uploadHereReason"
                @click="pickFiles('deskFiles')">Upload files</button>
              <button
                type="button"
                class="rx-btn rx-btn--outline-plain"
                :disabled="!canUploadHere"
                :title="canUploadHere ? 'Upload a folder into ' + currentNode.name : uploadHereReason"
                @click="pickFiles('deskFolder')">Upload a folder</button>
              <input ref="deskFiles" type="file" class="rx-hidden-input" multiple :accept="acceptAttr" tabindex="-1" aria-hidden="true" @change="onFilesPicked($event, currentNode)" />
              <input ref="deskFolder" type="file" class="rx-hidden-input" webkitdirectory multiple tabindex="-1" aria-hidden="true" @change="onFilesPicked($event, currentNode)" />
            </div>
          </div>
          <div v-if="selectedItems.length > 0" class="rx-bulk" role="toolbar" aria-label="Selected items">
            <span class="rx-bulk__count">{{ selectedItems.length }} selected</span>
            <button type="button" class="rx-bulk__btn" :disabled="actionPending" @click="bulkMove"><i class="fas fa-arrow-right"></i> Move to…</button>
            <button type="button" class="rx-bulk__btn" :disabled="actionPending || zipPending" @click="downloadZip"><i :class="zipPending ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i> Download (.zip)</button>
            <button v-if="bulkArchivable.length > 0" type="button" class="rx-bulk__btn" :disabled="actionPending" @click="bulkArchive"><i class="fas fa-archive"></i> Archive</button>
            <button v-if="bulkRestorable.length > 0" type="button" class="rx-bulk__btn" :disabled="actionPending" @click="bulkRestore"><i class="fas fa-undo"></i> Restore</button>
            <button type="button" class="rx-bulk__btn rx-bulk__btn--danger" :disabled="actionPending" @click="bulkDelete"><i class="far fa-trash-alt"></i> Delete</button>
            <button type="button" class="rx-bulk__btn rx-bulk__clear" :disabled="actionPending" @click="clearSelection">Clear</button>
          </div>
          <ContentsTable
            :items="desktopItems"
            :loading="currentLoading"
            :empty-text="desktopQuery.trim() ? 'No items in this folder match your search' : 'This folder is empty'"
            :creating="deskCreating"
            :renaming-id="deskRenamingId"
            :pending="actionPending"
            :error="(deskCreating || deskRenamingId) ? actionError : ''"
            :selected="selected"
            @toggle-select="toggleSelect"
            @select-all="selectAll"
            @create-save="onDeskCreateSave"
            @create-cancel="cancelDeskEdit"
            @rename-save="onDeskRenameSave"
            @rename-cancel="cancelDeskEdit"
            @action="onTableAction"
            @open="openItem" />
        </section>
      </div>
    </div>

    <JumpToFolder
      v-if="jumpOpen"
      :nodes="nodes"
      :current-id="currentId"
      :recents="recents"
      :focus-search="jumpFocus"
      @close="jumpOpen = false"
      @load="onLoadRequest"
      @navigate="onJumpNavigate"
      @navigate-recent="onJumpRecent" />

    <!-- Phone: Move to… destination picker -->
    <JumpToFolder
      v-if="moveItems && isPhone"
      mode="pick"
      :title="moveTitle"
      :nodes="nodes"
      :current-id="moveItems[0].id"
      :is-disabled="moveDisabled"
      :pending="actionPending"
      @close="closeMove"
      @load="onLoadRequest"
      @pick="onMovePick" />

    <!-- Desktop: Move to… destination picker -->
    <b-modal
      :visible="!!moveItems && !isPhone"
      :title="moveTitle"
      hide-footer
      centered
      :no-close-on-backdrop="actionPending"
      :no-close-on-esc="actionPending"
      @hide="onMoveModalHide">
      <template v-if="moveItems && !isPhone">
        <p class="rx-move__intro">Choose a new location for <b>{{ moveLabel }}</b>.</p>
        <div class="rx-move__tree">
          <FolderTree
            variant="desktop"
            :nodes="nodes"
            :current-id="moveSelectedId || ''"
            :expanded="moveExpanded"
            :is-disabled="moveDisabled"
            show-root
            @toggle="onMoveTreeToggle"
            @navigate="onMoveTreeSelect" />
        </div>
        <div v-if="actionError" class="rx-move__error" role="alert">{{ actionError }}</div>
        <div class="rx-move__footer">
          <button type="button" class="rx-btn rx-btn--outline-plain" :disabled="actionPending" @click="closeMove">Cancel</button>
          <button
            type="button"
            class="rx-btn rx-btn--solid"
            :disabled="!moveSelectedId || actionPending"
            @click="onMovePick(nodes[moveSelectedId])">{{ actionPending ? 'Moving…' : 'Move here' }}</button>
        </div>
      </template>
    </b-modal>

    <!-- Phone: floating action button -->
    <button
      v-if="showFab"
      type="button"
      :class="['rx-fab', phoneTrayVisible ? 'rx-fab--lifted' : '']"
      :aria-label="'Add to ' + currentNode.name"
      @click="openAddSheet"><i class="fas fa-plus"></i></button>
    <div v-if="phoneTrayVisible" class="rx-tray-spacer" aria-hidden="true"></div>

    <!-- Phone: folder / file actions sheet -->
    <BottomSheet v-if="sheet === 'actions' && sheetNode" :title="sheetNode.name" @close="closeSheet">
      <div class="rx-srows">
        <button
          v-for="a in sheetActions"
          :key="a.key"
          type="button"
          :class="['rx-srow', a.danger ? 'rx-srow--danger' : '']"
          :disabled="a.disabled"
          @click="onSheetAction(a.key)">
          <i :class="['rx-srow__icon', a.icon]"></i>
          <span class="rx-srow__text"><span class="rx-srow__title">{{ a.title }}</span><span v-if="a.sub" class="rx-srow__sub">{{ a.sub }}</span></span>
        </button>
        <button type="button" class="rx-srow rx-srow--cancel" @click="closeSheet">Cancel</button>
      </div>
    </BottomSheet>

    <!-- Phone: header overflow menu -->
    <BottomSheet v-if="sheet === 'overflow'" title="Options" @close="closeSheet">
      <div class="rx-srows">
        <button type="button" class="rx-srow" role="switch" :aria-checked="String(showArchived)" @click="setShowArchived(!showArchived)">
          <i class="fas fa-archive rx-srow__icon"></i>
          <span class="rx-srow__text rx-srow__text--grow"><span class="rx-srow__title">Show archived</span><span class="rx-srow__sub">Archived items appear dimmed</span></span>
          <span :class="['rx-switch', showArchived ? 'is-on' : '']" aria-hidden="true"><span class="rx-switch__knob"></span></span>
        </button>
        <button type="button" class="rx-srow rx-srow--cancel" @click="closeSheet">Done</button>
      </div>
    </BottomSheet>

    <!-- Phone: select mode "More" sheet -->
    <BottomSheet v-if="sheet === 'more'" :title="selectedItems.length + ' selected'" @close="closeSheet">
      <div class="rx-srows">
        <button v-if="bulkRestorable.length > 0" type="button" class="rx-srow" @click="bulkRestore">
          <i class="fas fa-undo rx-srow__icon"></i>
          <span class="rx-srow__text"><span class="rx-srow__title">Restore</span><span class="rx-srow__sub">{{ bulkRestorable.length }} archived {{ bulkRestorable.length === 1 ? 'item' : 'items' }}</span></span>
        </button>
        <button type="button" class="rx-srow rx-srow--danger" @click="bulkDelete">
          <i class="far fa-trash-alt rx-srow__icon"></i>
          <span class="rx-srow__text"><span class="rx-srow__title">Delete</span><span class="rx-srow__sub">Only archived files, and archived or empty folders</span></span>
        </button>
        <button type="button" class="rx-srow rx-srow--cancel" @click="closeSheet">Cancel</button>
      </div>
    </BottomSheet>

    <!-- Phone: select mode action bar -->
    <div v-if="isPhone && selectMode" class="rx-selbar" role="toolbar" aria-label="Selected items">
      <button type="button" class="rx-selbar__btn" :disabled="selectedItems.length === 0 || actionPending" @click="bulkMove">
        <i class="fas fa-arrow-right"></i><span>Move</span>
      </button>
      <button type="button" class="rx-selbar__btn rx-selbar__btn--tight" :disabled="selectedItems.length === 0 || actionPending || zipPending" @click="downloadZip">
        <i :class="zipPending ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i><span>Download (.zip)</span>
      </button>
      <button type="button" class="rx-selbar__btn" :disabled="bulkArchivable.length === 0 || actionPending" @click="bulkArchive">
        <i class="fas fa-archive"></i><span>Archive</span>
      </button>
      <button type="button" class="rx-selbar__btn" :disabled="selectedItems.length === 0 || actionPending" @click="openMoreSheet">
        <i class="fas fa-ellipsis-h"></i><span>More</span>
      </button>
    </div>

    <!-- Confirmation (desktop modal / phone sheet) -->
    <b-modal
      :visible="!!confirmBox && !isPhone"
      :title="confirmBox ? confirmBox.title : ''"
      hide-footer
      centered
      :no-close-on-backdrop="actionPending"
      :no-close-on-esc="actionPending"
      @hide="onConfirmHide">
      <div v-if="confirmBox && !isPhone" class="rx-confirm">
        <p class="rx-confirm__msg">{{ confirmBox.message }}</p>
        <div v-if="confirmBox.skipped.length > 0" class="rx-confirm__skipped">
          <div class="rx-confirm__label">Will be skipped:</div>
          <ul class="rx-confirm__list">
            <li v-for="s in confirmBox.skipped" :key="s.id">{{ s.name }} <span class="rx-confirm__reason">({{ s.reason }})</span></li>
          </ul>
        </div>
        <div class="rx-move__footer">
          <button type="button" class="rx-btn rx-btn--outline-plain" :disabled="actionPending" @click="closeConfirm">{{ confirmBox.okLabel ? 'Cancel' : 'Close' }}</button>
          <button
            v-if="confirmBox.okLabel"
            type="button"
            :class="['rx-btn', confirmBox.danger ? 'rx-btn--danger' : 'rx-btn--solid']"
            :disabled="actionPending"
            @click="runConfirm">{{ actionPending ? 'Working…' : confirmBox.okLabel }}</button>
        </div>
      </div>
    </b-modal>
    <BottomSheet v-if="confirmBox && isPhone" :title="confirmBox.title" :locked="actionPending" @close="closeConfirm">
      <div class="rx-confirm rx-confirm--phone">
        <p class="rx-confirm__msg">{{ confirmBox.message }}</p>
        <div v-if="confirmBox.skipped.length > 0" class="rx-confirm__skipped">
          <div class="rx-confirm__label">Will be skipped:</div>
          <ul class="rx-confirm__list">
            <li v-for="s in confirmBox.skipped" :key="s.id">{{ s.name }} <span class="rx-confirm__reason">({{ s.reason }})</span></li>
          </ul>
        </div>
        <div class="rx-nameform__buttons">
          <button type="button" class="rx-nameform__btn" :disabled="actionPending" @click="closeConfirm">{{ confirmBox.okLabel ? 'Cancel' : 'Close' }}</button>
          <button
            v-if="confirmBox.okLabel"
            type="button"
            :class="['rx-nameform__btn', confirmBox.danger ? 'rx-nameform__btn--danger' : 'rx-nameform__btn--primary']"
            :disabled="actionPending"
            @click="runConfirm">{{ actionPending ? 'Working…' : confirmBox.okLabel }}</button>
        </div>
      </div>
    </BottomSheet>

    <!-- Bulk failure report (desktop modal / phone sheet) -->
    <b-modal
      :visible="!!failureReport && !isPhone"
      :title="failureReport ? failureReport.title : ''"
      ok-only
      ok-title="Close"
      ok-variant="secondary"
      centered
      @hide="failureReport = null">
      <ul v-if="failureReport && !isPhone" class="rx-confirm__list">
        <li v-for="f in failureReport.items" :key="f.id"><b>{{ f.name }}</b> <span class="rx-confirm__reason">— {{ f.reason }}</span></li>
      </ul>
    </b-modal>
    <BottomSheet v-if="failureReport && isPhone" :title="failureReport.title" @close="failureReport = null">
      <div class="rx-confirm rx-confirm--phone">
        <ul class="rx-confirm__list">
          <li v-for="f in failureReport.items" :key="f.id"><b>{{ f.name }}</b> <span class="rx-confirm__reason">— {{ f.reason }}</span></li>
        </ul>
        <div class="rx-nameform__buttons">
          <button type="button" class="rx-nameform__btn" @click="failureReport = null">Close</button>
        </div>
      </div>
    </BottomSheet>

    <!-- Phone: rename / new folder sheet -->
    <BottomSheet v-if="sheet === 'name' && sheetNode" :title="nameSheetTitle" :locked="actionPending" @close="closeSheet">
      <form class="rx-nameform" @submit.prevent="saveNameSheet">
        <input
          ref="nameInput"
          v-model="nameValue"
          type="text"
          class="rx-nameform__input"
          :placeholder="nameMode === 'create' ? 'Folder name — use / to nest' : (sheetNode.kind === 'file' ? 'File name' : 'Folder name')"
          :aria-label="nameMode === 'create' ? 'New folder name. Use / to nest folders' : (sheetNode.kind === 'file' ? 'File name' : 'Folder name')"
          :maxlength="nameMode === 'create' ? 400 : 200"
          autocomplete="off"
          :disabled="actionPending" />
        <div v-if="actionError" class="rx-nameform__error" role="alert">{{ actionError }}</div>
        <div class="rx-nameform__buttons">
          <button type="button" class="rx-nameform__btn" :disabled="actionPending" @click="closeSheet">Cancel</button>
          <button type="submit" class="rx-nameform__btn rx-nameform__btn--primary" :disabled="actionPending">{{ actionPending ? 'Saving…' : (nameMode === 'create' ? 'Create' : 'Save') }}</button>
        </div>
      </form>
    </BottomSheet>

    <!-- Phone: FAB "Add to" sheet -->
    <BottomSheet v-if="sheet === 'add' && sheetNode" :title="'Add to ' + sheetNode.name" @close="closeSheet">
      <div class="rx-srows">
        <button type="button" class="rx-srow" @click="openNameSheet('create', sheetNode)">
          <i class="fas fa-folder-plus rx-srow__icon"></i><span class="rx-srow__text"><span class="rx-srow__title">New folder</span></span>
        </button>
        <div class="rx-adddest">
          <span class="rx-adddest__text">
            <template v-if="addDestNode">Uploading to <b>{{ addDestNode.name }}</b></template>
            <template v-else>Choose a folder to upload into</template>
          </span>
          <button type="button" class="rx-adddest__change" @click="openDestPicker">Change</button>
        </div>
        <button type="button" class="rx-srow" :disabled="!addDestNode" @click="pickFiles('phoneCamera')">
          <i class="fas fa-camera rx-srow__icon"></i><span class="rx-srow__text"><span class="rx-srow__title">Take photo</span></span>
        </button>
        <button type="button" class="rx-srow" :disabled="!addDestNode" @click="pickFiles('phoneLibrary')">
          <i class="far fa-images rx-srow__icon"></i><span class="rx-srow__text"><span class="rx-srow__title">Photo library</span><span class="rx-srow__sub">Photos and videos</span></span>
        </button>
        <button type="button" class="rx-srow" :disabled="!addDestNode" @click="pickFiles('phoneBrowse')">
          <i class="far fa-file rx-srow__icon"></i><span class="rx-srow__text"><span class="rx-srow__title">Browse files</span><span class="rx-srow__sub">Documents, images and videos</span></span>
        </button>
        <button type="button" class="rx-srow rx-srow--cancel" @click="closeSheet">Cancel</button>
      </div>
    </BottomSheet>

    <!-- Phone: upload inputs (outside the sheet so they survive it closing) -->
    <template v-if="isPhone">
      <input ref="phoneCamera" type="file" class="rx-hidden-input" accept="image/*" capture="environment" tabindex="-1" aria-hidden="true" @change="onFilesPicked($event, addDestNode)" />
      <input ref="phoneLibrary" type="file" class="rx-hidden-input" accept="image/*,video/*" multiple tabindex="-1" aria-hidden="true" @change="onFilesPicked($event, addDestNode)" />
      <input ref="phoneBrowse" type="file" class="rx-hidden-input" :accept="acceptAttr" multiple tabindex="-1" aria-hidden="true" @change="onFilesPicked($event, addDestNode)" />
    </template>

    <!-- Phone: choose upload destination -->
    <JumpToFolder
      v-if="destPickOpen && isPhone"
      mode="pick"
      title="Upload to…"
      pick-prompt="Upload to"
      pick-button="Choose"
      :nodes="nodes"
      :current-id="addDestId || currentId"
      :is-disabled="uploadDestDisabled"
      @close="closeDestPicker"
      @load="onLoadRequest"
      @pick="onDestPick" />

    <UploadTray :is-phone="isPhone" :hidden="isPhone && selectMode" />

    <!-- F4b: upload name conflict prompt -->
    <ConflictPrompt
      :conflict="conflictAsk ? conflictAsk.conflict : null"
      :remaining="conflictAsk ? conflictAsk.remaining : 0"
      @answer="onConflictAnswer" />

    <!-- F4b: version history (desktop side panel / phone full screen) -->
    <VersionHistory
      :node="versionNode"
      :is-phone="isPhone"
      @close="versionNodeId = null"
      @restored="onVersionRestored" />

    <vue-gallery-slideshow :images="imagesForGallery" :index="galleryIndex" @close="galleryIndex = null"></vue-gallery-slideshow>
    <VideoPlayerModal :visible.sync="videoPlayerVisible" :videos="videoPlaylist" :start-index="activeVideoIndex" />
  </div>
</template>

<script>
import VueGallerySlideshow from 'vue-gallery-slideshow'
import VideoPlayerModal from '../VideoPlayerModal.vue'
import FolderTree from './FolderTree.vue'
import ContentsTable from './ContentsTable.vue'
import PhoneFolderView from './PhoneFolderView.vue'
import JumpToFolder from './JumpToFolder.vue'
import BottomSheet from './BottomSheet.vue'
import UploadTray from './UploadTray.vue'
import ConflictPrompt from './ConflictPrompt.vue'
import VersionHistory from './VersionHistory.vue'
import fileSaver from 'file-saver'
import { isLambdaDocsApi } from '@/utils/uploadLimits'
import { fileNameFromDisposition } from './fileVersions'
import {
  ROOT_ID, createRootNode, normalizeResponse, sortNodes, pathOf, readRecents, pushRecent,
  canRenameOrMove, canCreateIn, apiFolderId, apiPropertyId, cleanFolderPath,
  readShowArchived, writeShowArchived, canArchive, canMoveItem, canSelect, deleteBlockReason, deleteConfirmText,
  fileApiId, filePropertyId, isMoveDestinationDisabledFor, itemActions,
  canUploadTo, uploadBlockReason, folderLabel, canRenameItem
} from './resourcesData'
import {
  uploadQueueState, loadAcceptedTypes, acceptAttribute, enqueueFiles, onFolderChanged,
  entriesFromFileList, entriesFromDrop, isFileDrag, setConflictPrompter, setKnownNamesProvider
} from './uploadQueue'

var GENERIC_FAILURE = 'The request could not be completed. Please try again.'
var VERBS = { archive: 'Archived', restore: 'Restored', 'delete': 'Deleted', move: 'Moved' }

var PHONE_MAX = 768
var ZIP_FALLBACK_NAME = 'Resources.zip'
var ZIP_LAMBDA_MESSAGE = 'The zip could not be downloaded. This test environment (agent-dev) runs the Documents API on AWS Lambda, ' +
  'which limits responses to about 6 MB, so larger zips fail here. Select fewer or smaller files and try again. ' +
  'This is a limitation of the test environment only and does not affect dev, test or production.'
var LOCATE_MAX_LOADS = 60

function plural (n, word) {
  return n + ' ' + word + (n === 1 ? '' : 's')
}

export default {
  name: 'ResourcesExplorer',
  components: { VueGallerySlideshow, VideoPlayerModal, FolderTree, ContentsTable, PhoneFolderView, JumpToFolder, BottomSheet, UploadTray, ConflictPrompt, VersionHistory },
  data () {
    var nodes = {}
    nodes[ROOT_ID] = createRootNode()
    return {
      nodes: nodes,
      currentId: ROOT_ID,
      isPhone: typeof window !== 'undefined' ? window.innerWidth < PHONE_MAX : false,
      sortKey: 'name',
      jumpOpen: false,
      jumpFocus: false,
      recents: readRecents(),
      desktopExpanded: {},
      showAllProps: {},
      desktopQuery: '',
      galleryIndex: null,
      videoPlayerVisible: false,
      activeVideoIndex: 0,
      // ---- F2 folder editing ----
      actionPending: false,
      actionError: '',
      deskCreating: false,
      deskRenamingId: null,
      pendingCreateFor: null,
      moveItems: null, // array of nodes being moved (folders and/or files)
      moveSelectedId: null,
      moveExpanded: {},
      sheet: null, // 'actions' | 'name' | 'add' | 'overflow' | 'more'
      sheetNode: null,
      nameMode: 'create', // 'create' | 'rename'
      nameValue: '',
      // ---- F4a uploads ----
      uploadQueue: uploadQueueState,
      acceptAttr: '',
      addDestId: null, // phone Add sheet upload destination
      destPickOpen: false,
      contentDragOver: false,
      // ---- F4b ----
      conflictAsk: null, // { conflict, remaining, resolve }
      versionNodeId: null,
      zipPending: false,
      // ---- F3 archive / delete / multi-select ----
      showArchived: readShowArchived(),
      selected: {},
      selectMode: false, // phone only
      confirmBox: null, // { title, message, skipped: [{id,name,reason}], okLabel, danger, run: fn }
      failureReport: null // { title, items: [{id,name,reason}] }
    }
  },
  computed: {
    canCreateHere () {
      return canCreateIn(this.currentNode) && !this.currentNode.isArchived
    },
    showFab () {
      return this.isPhone && this.canCreateHere && !this.jumpOpen && !this.moveItems && !this.sheet && !this.selectMode && !this.confirmBox && !this.destPickOpen
    },
    canUploadHere () {
      return canUploadTo(this.currentNode)
    },
    uploadHereReason () {
      return uploadBlockReason(this.currentNode)
    },
    addDestNode () {
      var n = this.addDestId ? this.nodes[this.addDestId] : null
      return n && canUploadTo(n) ? n : null
    },
    uploadDestDisabled () {
      return function (n) { return !canUploadTo(n) }
    },
    uploadTargetOk () {
      return function (n) { return canUploadTo(n) }
    },
    phoneTrayVisible () {
      return this.isPhone && !this.selectMode && this.uploadQueue.items.length > 0
    },
    sheetActions () {
      return itemActions(this.sheetNode)
    },
    nameSheetTitle () {
      if (!this.sheetNode) return ''
      if (this.nameMode === 'rename') return this.sheetNode.kind === 'file' ? 'Rename file' : 'Rename folder'
      return 'New folder in ' + this.sheetNode.name
    },
    versionNode () {
      var n = this.versionNodeId ? this.nodes[this.versionNodeId] : null
      return n && n.kind === 'file' && !n.deleted ? n : null
    },
    moveDisabled () {
      var nodes = this.nodes
      var moving = this.moveItems || []
      return function (n) { return isMoveDestinationDisabledFor(nodes, moving, n) }
    },
    moveTitle () {
      if (!this.moveItems) return 'Move to…'
      return this.moveItems.length === 1 ? 'Move to…' : 'Move ' + this.moveItems.length + ' items to…'
    },
    moveLabel () {
      if (!this.moveItems) return ''
      return this.moveItems.length === 1 ? this.moveItems[0].name : plural(this.moveItems.length, 'item')
    },
    selectedItems () {
      var sel = this.selected
      return this.currentChildren.filter(function (n) { return !!sel[n.id] && canSelect(n) })
    },
    bulkArchivable () {
      return this.selectedItems.filter(function (n) { return !n.isArchived && canArchive(n) })
    },
    bulkRestorable () {
      return this.selectedItems.filter(function (n) { return n.isArchived && canArchive(n) })
    },
    currentNode () {
      return this.nodes[this.currentId] || this.nodes[ROOT_ID]
    },
    currentLoading () {
      return !this.currentNode.loaded
    },
    pathNodes () {
      return pathOf(this.nodes, this.currentNode.id)
    },
    currentChildren () {
      var self = this
      return this.currentNode.childIds
        .map(function (id) { return self.nodes[id] })
        .filter(function (n) { return !!n })
    },
    phoneItems () {
      return sortNodes(this.currentChildren.slice(), this.sortKey)
    },
    desktopItems () {
      var q = this.desktopQuery.trim().toLowerCase()
      var list = this.currentChildren.slice()
      if (q) {
        list = list.filter(function (n) { return n.name.toLowerCase().indexOf(q) !== -1 })
      }
      return sortNodes(list, 'name')
    },
    currentImages () {
      return sortNodes(this.currentChildren.slice(), this.isPhone ? this.sortKey : 'name')
        .filter(function (n) { return n.kind === 'file' && n.mediaType === 'Image' })
    },
    currentVideos () {
      return sortNodes(this.currentChildren.slice(), this.isPhone ? this.sortKey : 'name')
        .filter(function (n) { return n.kind === 'file' && n.mediaType === 'Video' })
    },
    // Same mapping as Resources.vue imagesForGallery
    imagesForGallery () {
      return this.currentImages.map(function (n) {
        return { url: n.raw.publicUrl, id: n.raw.id, alt: (n.raw.notes || n.raw.name) }
      })
    },
    // Same mapping as Resources.vue videoPlaylist
    videoPlaylist () {
      return this.currentVideos.map(function (n) {
        var v = n.raw
        return {
          url: v.processedVideoPublicUrl || v.publicUrl,
          contentType: v.processedVideoPublicUrl ? 'video/mp4' : (v.contentType || 'video/mp4'),
          name: v.name,
          thumbnailUrl: v.thumbnailPublicUrl
        }
      })
    }
  },
  watch: {
    '$route.query': function (q) {
      // Back gesture / browser navigation while the overlay is open should close it
      this.jumpOpen = false
      if (!this.actionPending) {
        this.sheet = null
        this.moveItems = null
        this.confirmBox = null
      }
      // Phone select mode lives in history (?select=1) so the back gesture exits it
      if (this.selectMode && !(q && q.select)) {
        this.selectMode = false
        this.selected = {}
      }
      this.syncFromRoute()
    }
  },
  created () {
    this.loadPromises = {}
    var q = this.$route.query || {}
    if (q.select) {
      // A stale select-mode entry (reload / shared link): drop it
      var clean = Object.assign({}, q)
      delete clean.select
      this.$router.replace({ path: this.$route.path, query: clean }, function () {}, function () {})
    }
    this.syncFromRoute()
    var self = this
    loadAcceptedTypes(this.$store).then(function () {
      self.acceptAttr = acceptAttribute()
    })
    // Refresh loaded folders as uploads land (queue outlives this component)
    this.offUploads = onFolderChanged(function (id) { self.onUploadFolderChanged(id) })
    // F4b: name-conflict dialog + names already in a folder (for "Keep both")
    setConflictPrompter(function (conflict, remaining) { return self.askConflict(conflict, remaining) })
    setKnownNamesProvider(function (folderId) { return self.knownFileNames(folderId) })
  },
  mounted () {
    this.onResize = this.onResize.bind(this)
    window.addEventListener('resize', this.onResize)
    // Desktop drag-and-drop: stop the browser from opening files dropped outside a target
    this.onWindowDragOver = this.onWindowDragOver.bind(this)
    this.onWindowDrop = this.onWindowDrop.bind(this)
    window.addEventListener('dragover', this.onWindowDragOver)
    window.addEventListener('drop', this.onWindowDrop)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    window.removeEventListener('dragover', this.onWindowDragOver)
    window.removeEventListener('drop', this.onWindowDrop)
    if (this.offUploads) this.offUploads()
    setConflictPrompter(null)
    setKnownNamesProvider(null)
    if (this.conflictAsk) this.onConflictAnswer({ choice: 'keep', applyAll: false })
  },
  methods: {
    onResize () {
      var phone = window.innerWidth < PHONE_MAX
      if (phone !== this.isPhone) {
        this.isPhone = phone
        if (!phone) {
          this.jumpOpen = false
          this.destPickOpen = false
          this.sheet = null
          if (this.selectMode) this.exitSelectMode()
        } else {
          this.cancelDeskEdit()
          this.selected = {}
        }
      }
    },

    // ---------- data loading (existing store actions only) ----------
    // force = refetch an already loaded folder (keeps loaded child nodes and tree expansion state)
    loadChildren (id, force) {
      var self = this
      var node = this.nodes[id]
      if (!node) return Promise.resolve(null)
      if (this.loadPromises[id]) {
        if (!force) return this.loadPromises[id]
        return this.loadPromises[id].then(function () { return self.loadChildren(id, true) })
      }
      if (node.loaded && !force) return Promise.resolve(node)

      var wasLoaded = node.loaded
      node.loading = true
      var request
      if (id === ROOT_ID) {
        request = this.$store.dispatch('resources/getResources', { eventId: undefined, showArchived: this.showArchived })
      } else {
        request = this.$store.dispatch('resources/getResourcesByFolderId', {
          folderId: node.id,
          folderType: node.folderType,
          propertyId: node.propertyId,
          eventId: undefined,
          showArchived: this.showArchived
        })
      }
      var p = request.then(function (result) {
        if ((result === undefined || result === null) && wasLoaded) {
          // Keep showing the previous contents if a refresh fails
          node.loading = false
          delete self.loadPromises[id]
          self.$toasted.error('Unable to refresh folder contents', { duration: 4000 })
          return node
        }
        var norm = normalizeResponse(result, node, self.showArchived)
        var childIds = []
        norm.folders.forEach(function (f) {
          var existing = self.nodes[f.id]
          if (existing && existing.loaded) {
            existing.childCount = f.childCount
            existing.parentId = node.id
            existing.name = f.name
            existing.isEditable = f.isEditable
            existing.isArchived = f.isArchived
            existing.modified = f.modified
            existing.modifiedBy = f.modifiedBy
            existing.raw = f.raw
          } else {
            self.$set(self.nodes, f.id, f)
          }
          childIds.push(f.id)
        })
        norm.files.forEach(function (f) {
          self.$set(self.nodes, f.id, f)
          childIds.push(f.id)
        })
        node.childIds = childIds
        node.folderCount = norm.folders.length
        node.fileCount = norm.files.length
        node.loaded = result !== undefined && result !== null
        node.loading = false
        delete self.loadPromises[id]
        if (!node.loaded) {
          self.$toasted.error('Unable to load folder contents', { duration: 4000 })
        }
        return node
      }, function () {
        node.loading = false
        delete self.loadPromises[id]
        return node
      })
      this.loadPromises[id] = p
      return p
    },

    onLoadRequest (node) {
      this.loadChildren(node.id)
    },

    // Breadth-first lazy load until the requested folder is found (used for deep links / refresh).
    locate (id, rt, rid) {
      var self = this
      var loads = 0
      var isProperty = String(rt || '').toLowerCase() === 'property'
      var queue = []

      function enqueueChildren (parent) {
        parent.childIds.forEach(function (cid) {
          var c = self.nodes[cid]
          if (!c || c.kind !== 'folder') return
          if (isProperty) {
            if (c.resourceType !== 'Property') return
            if (rid && c.propertyId !== null && String(c.propertyId) !== String(rid)) return
          } else if (c.resourceType === 'Property') {
            return
          }
          queue.push(c.id)
        })
      }

      function step () {
        if (self.nodes[id]) return Promise.resolve(true)
        if (queue.length === 0 || loads >= LOCATE_MAX_LOADS) return Promise.resolve(false)
        var next = queue.shift()
        loads++
        return self.loadChildren(next).then(function (n) {
          if (n && n.loaded) enqueueChildren(n)
          return step()
        })
      }

      return this.loadChildren(ROOT_ID).then(function (root) {
        enqueueChildren(root)
        return step()
      })
    },

    // Last resort for an unreachable deep link: load the folder directly as a child of root.
    loadOrphan (id, rt, rid) {
      var recent = this.recents.filter(function (r) { return r.id === id })[0]
      var name = recent ? recent.label.split(' › ').pop() : 'Folder'
      var isProperty = String(rt || '').toLowerCase() === 'property'
      var node = createRootNode()
      node.id = id
      node.name = name
      node.parentId = ROOT_ID
      node.folderType = isProperty ? 'Property' : 'Company'
      node.resourceType = isProperty ? 'Property' : 'Company'
      node.propertyId = rid || null
      node.resourceId = rid || null
      this.$set(this.nodes, id, node)
      return this.loadChildren(id)
    },

    // ---------- routing ----------
    syncFromRoute () {
      var self = this
      var q = this.$route.query || {}
      var id = q.folder ? String(q.folder) : null
      if (!id) {
        this.setCurrent(ROOT_ID)
        return
      }
      if (this.nodes[id]) {
        this.setCurrent(id)
        return
      }
      this.locate(id, q.rt, q.rid).then(function (found) {
        if (self.$route.query.folder !== q.folder) return
        if (found) {
          self.setCurrent(id)
        } else {
          self.loadOrphan(id, q.rt, q.rid).then(function (n) {
            if (n && n.loaded) {
              self.setCurrent(id)
            } else {
              self.$delete(self.nodes, id)
              self.$toasted.error('That folder could not be found', { duration: 4000 })
              self.setCurrent(ROOT_ID)
            }
          })
        }
      })
    },

    setCurrent (id) {
      var self = this
      if (id !== this.currentId) {
        this.cancelDeskEdit()
        this.selected = {}
      }
      this.currentId = id
      this.loadChildren(id)
      if (this.pendingCreateFor === id) {
        this.pendingCreateFor = null
        this.$nextTick(function () {
          if (canCreateIn(self.currentNode)) self.deskCreating = true
        })
      }
      this.revealCurrent()
    },

    // Keep the current folder visible in the desktop tree and remember it in recents
    revealCurrent () {
      var self = this
      var id = this.currentId
      var chain = pathOf(this.nodes, id)
      chain.forEach(function (n) {
        if (n.id !== ROOT_ID && n.id !== id) {
          self.$set(self.desktopExpanded, n.id, true)
          if (n.isPropertyGroup) self.$set(self.showAllProps, n.id, true)
        }
      })
      if (id !== ROOT_ID) {
        var node = this.nodes[id]
        var labelNodes = chain.filter(function (n) { return n.id !== ROOT_ID }).slice(-2)
        this.recents = pushRecent({
          id: id,
          rt: node.resourceType,
          rid: node.propertyId,
          label: labelNodes.map(function (n) { return n.name }).join(' › ')
        })
      }
    },

    queryFor (node) {
      if (!node || node.id === ROOT_ID) return {}
      var q = { folder: node.id, rt: node.resourceType }
      if (node.propertyId !== null && node.propertyId !== undefined) q.rid = String(node.propertyId)
      return q
    },

    pushQuery (query) {
      var cur = this.$route.query || {}
      if (String(cur.folder || '') === String(query.folder || '')) return
      this.$router.push({ path: this.$route.path, query: query }, function () {}, function () {})
    },

    navigateTo (node) {
      if (!node || node.kind !== 'folder') return
      this.pushQuery(this.queryFor(node))
    },

    goToParent () {
      var parentId = this.currentNode.parentId || ROOT_ID
      this.navigateTo(this.nodes[parentId] || this.nodes[ROOT_ID])
    },

    // ---------- UI handlers ----------
    openJump (focusSearch) {
      var self = this
      this.jumpFocus = focusSearch === true
      this.jumpOpen = true
      // Make property names and top-level subfolders searchable.
      this.loadChildren(ROOT_ID).then(function (root) {
        root.childIds.forEach(function (cid) {
          var c = self.nodes[cid]
          if (c && c.kind === 'folder') self.loadChildren(cid)
        })
      })
    },

    onJumpNavigate (node) {
      this.jumpOpen = false
      this.navigateTo(node)
    },

    onJumpRecent (r) {
      this.jumpOpen = false
      var q = { folder: r.id, rt: r.rt }
      if (r.rid !== null && r.rid !== undefined) q.rid = String(r.rid)
      this.pushQuery(q)
    },

    toggleSort () {
      this.sortKey = this.sortKey === 'name' ? 'modified' : 'name'
    },

    onTreeToggle (node) {
      var open = !this.desktopExpanded[node.id]
      this.$set(this.desktopExpanded, node.id, open)
      if (open) this.loadChildren(node.id)
    },

    onShowAll (parentId) {
      this.$set(this.showAllProps, parentId, true)
    },

    collapseAll () {
      this.desktopExpanded = {}
      this.showAllProps = {}
    },

    // ---------- F2: folder create / rename / move ----------
    runFolderCommand (action, args) {
      var self = this
      if (this.actionPending) return Promise.resolve(null)
      this.actionPending = true
      this.actionError = ''
      return this.$store.dispatch('resources/' + action, args).then(function (res) {
        self.actionPending = false
        var r = res || { success: false, failureReason: GENERIC_FAILURE }
        if (r.success !== true) self.actionError = r.failureReason || GENERIC_FAILURE
        return r
      }, function () {
        self.actionPending = false
        self.actionError = GENERIC_FAILURE
        return { success: false, failureReason: GENERIC_FAILURE }
      })
    },

    // Refetch the given loaded folders plus any folders the API reports as affected.
    refreshAfter (ids, response) {
      var self = this
      var all = ids.concat((response && response.affectedFolderIds) || [])
      var seen = {}
      all.forEach(function (raw) {
        var id = raw === null || raw === undefined || raw === '' ? ROOT_ID : String(raw)
        if (seen[id]) return
        seen[id] = true
        var n = self.nodes[id]
        if (n && n.kind === 'folder' && n.loaded && !n.deleted && !(n.isArchived && !self.showArchived)) self.loadChildren(id, true)
      })
    },

    createFolder (parent, rawPath) {
      var self = this
      if (this.actionPending) return Promise.resolve(false)
      var path = cleanFolderPath(rawPath)
      if (!path) {
        this.actionError = String(rawPath || '').trim() ? 'Folder names cannot be empty. Check the "/" separators.' : 'Enter a folder name.'
        return Promise.resolve(false)
      }
      if (!canCreateIn(parent)) {
        this.actionError = 'Folders cannot be created here.'
        return Promise.resolve(false)
      }
      return this.runFolderCommand('createFolderPath', {
        propertyId: apiPropertyId(parent),
        parentId: apiFolderId(parent),
        path: path
      }).then(function (r) {
        if (!r || r.success !== true) return false
        self.refreshAfter([parent.id, parent.parentId], r)
        self.$toasted.success(path.indexOf('/') === -1 ? 'Folder created' : 'Folders created', { duration: 2500 })
        return true
      })
    },

    renameFolder (node, rawName) {
      var self = this
      if (this.actionPending) return Promise.resolve(false)
      var name = String(rawName || '').trim()
      if (!name) {
        this.actionError = 'Enter a folder name.'
        return Promise.resolve(false)
      }
      if (name === node.name) return Promise.resolve(true)
      return this.runFolderCommand('renameFolder', {
        propertyId: apiPropertyId(node),
        folderId: node.id,
        name: name
      }).then(function (r) {
        if (!r || r.success !== true) return false
        node.name = (r.folder && r.folder.name) || name
        self.refreshAfter([node.parentId], r)
        if (pathOf(self.nodes, self.currentId).indexOf(node) !== -1) self.revealCurrent()
        self.$toasted.success('Folder renamed', { duration: 2500 })
        return true
      })
    },

    // ---------- F4b: file rename / versions / zip / name conflicts ----------
    renameFile (node, rawName) {
      var self = this
      if (this.actionPending) return Promise.resolve(false)
      var name = String(rawName || '').trim()
      if (!name) {
        this.actionError = 'Enter a file name.'
        return Promise.resolve(false)
      }
      if (name === node.name) return Promise.resolve(true)
      return this.runFolderCommand('renameFile', {
        propertyId: filePropertyId(node),
        fileId: fileApiId(node),
        name: name
      }).then(function (r) {
        if (!r || r.success !== true) return false
        node.name = (r.file && r.file.name) || name
        self.refreshAfter([node.parentId], r)
        self.$toasted.success('File renamed', { duration: 2500 })
        return true
      })
    },

    openVersions (node) {
      if (!node || node.kind !== 'file') return
      this.versionNodeId = node.id
    },

    onVersionRestored (file) {
      var node = this.versionNode
      if (!node) return
      if (file && typeof file === 'object') node.raw = file
      this.refreshAfter([node.parentId], null)
    },

    // uploadQueue conflict prompter: one dialog at a time (the queue serializes calls)
    askConflict (conflict, remaining) {
      var self = this
      return new Promise(function (resolve) {
        self.conflictAsk = { conflict: conflict, remaining: remaining, resolve: resolve }
      })
    },

    onConflictAnswer (answer) {
      var ask = this.conflictAsk
      this.conflictAsk = null
      if (ask) ask.resolve(answer)
    },

    // File names (display + stored) of a loaded folder, for unique "Keep both" names
    knownFileNames (folderId) {
      var self = this
      var folder = this.nodes[String(folderId)]
      if (!folder || !folder.loaded) return []
      var names = []
      folder.childIds.forEach(function (cid) {
        var n = self.nodes[cid]
        if (!n || n.kind !== 'file') return
        names.push(n.name)
        if (n.raw && n.raw.fileName) names.push(n.raw.fileName)
      })
      return names
    },

    downloadZip () {
      var self = this
      var items = this.selectedItems.slice()
      if (items.length === 0 || this.zipPending) return
      var folderIds = []
      var fileIds = []
      items.forEach(function (n) {
        if (n.kind === 'file') fileIds.push(fileApiId(n))
        else folderIds.push(n.id)
      })
      this.zipPending = true
      var toast = this.$toasted.show('Preparing download (.zip)…', { duration: null, position: 'top-center' })
      function done () {
        self.zipPending = false
        try { toast.goAway(0) } catch (e) { /* ignore */ }
      }
      this.$store.dispatch('resources/downloadZip', {
        propertyId: apiPropertyId(this.currentNode),
        folderIds: folderIds,
        fileIds: fileIds
      }).then(function (r) {
        done()
        fileSaver.saveAs(r.blob, fileNameFromDisposition(r.contentDisposition, ZIP_FALLBACK_NAME))
      }, function (e) {
        done()
        var status = e && e.response ? e.response.status : null
        if (isLambdaDocsApi() && (status === null || status === 413 || status === 502 || status === 504)) {
          self.$toasted.error(ZIP_LAMBDA_MESSAGE, {
            duration: null,
            position: 'top-center',
            action: { text: 'Close', onClick: function (ev, t) { t.goAway(0) } }
          })
        } else {
          self.$toasted.error('The zip could not be downloaded. Please try again.', { duration: 5000 })
        }
      })
    },

    // ---------- F3: archive / restore / delete / move (single + bulk) ----------

    // One request for one item. Always resolves to { success, failureReason, affectedFolderIds }.
    itemRequest (type, node, dest) {
      var self = this
      var req
      var raw = node.raw || {}
      var isFile = node.kind === 'file'
      if (type === 'archive' || type === 'restore') {
        req = isFile
          ? this.$store.dispatch('resources/' + type + 'File', { propertyId: filePropertyId(node), fileId: fileApiId(node) })
          : this.$store.dispatch('resources/' + type + 'Folder', { propertyId: apiPropertyId(node), folderId: node.id })
      } else if (type === 'delete' && !isFile) {
        req = this.$store.dispatch('resources/deleteFolder', { propertyId: apiPropertyId(node), folderId: node.id })
      } else if (type === 'delete') {
        // Existing per-type delete actions (same calls/args as the classic Resources.vue)
        var propertyId = raw.propertyId === undefined ? null : raw.propertyId
        if (node.mediaType === 'Image') {
          req = this.$store.dispatch('resources/deleteImage', { imageId: raw.id, propertyId: propertyId, eventId: raw.eventId, name: raw.name, shouldPermanentlyDelete: true })
        } else if (node.mediaType === 'Video') {
          req = this.$store.dispatch('resources/deleteVideo', { videoId: raw.id, propertyId: propertyId, eventId: raw.eventId, name: raw.name, shouldPermanentlyDelete: true })
        } else {
          req = this.$store.dispatch('resources/deleteDocument', { documentId: raw.id, propertyId: propertyId, fileName: raw.name, eventId: raw.eventId, name: raw.name, shouldPermanentlyDelete: true })
        }
      } else if (type === 'move' && !isFile) {
        req = this.$store.dispatch('resources/moveFolder', { propertyId: apiPropertyId(node), folderId: node.id, targetParentId: apiFolderId(dest) })
      } else if (type === 'move') {
        // Existing per-type move actions; these resolve to the axios response
        var pid = raw.propertyId === undefined ? null : raw.propertyId
        var args = { propertyId: pid, eventId: raw.eventId, folderId: dest.id }
        var action = 'moveDocument'
        if (node.mediaType === 'Image') {
          action = 'moveImage'
          args.imageId = raw.id
        } else if (node.mediaType === 'Video') {
          action = 'moveVideo'
          args.videoId = raw.id
        } else {
          args.documentId = raw.id
        }
        req = this.$store.dispatch('resources/' + action, args).then(function (res) { return res && res.data })
      } else {
        return Promise.resolve({ success: false, failureReason: 'Not supported' })
      }
      return req.then(function (r) {
        if (r && r.success === true) return r
        return { success: false, failureReason: (r && r.failureReason) || GENERIC_FAILURE }
      }, function () {
        return { success: false, failureReason: GENERIC_FAILURE }
      })
    },

    // Runs `type` for each item sequentially, continuing past failures.
    runItems (type, items, dest) {
      var self = this
      if (this.actionPending || items.length === 0) return Promise.resolve(null)
      this.actionPending = true
      this.actionError = ''
      var results = []
      var i = 0
      function next () {
        if (i >= items.length) return Promise.resolve()
        var item = items[i++]
        var oldParentId = item.parentId || ROOT_ID
        return self.itemRequest(type, item, dest).then(function (r) {
          results.push({ item: item, r: r, oldParentId: oldParentId })
          if (r.success) self.applyLocal(type, item, dest)
          return next()
        })
      }
      return next().then(function () {
        self.actionPending = false
        self.afterItems(type, results, dest)
        return results
      })
    },

    // Immediate local update after a successful request (refetches reconcile afterwards).
    applyLocal (type, node, dest) {
      var parent = this.nodes[node.parentId || ROOT_ID]
      function detach () {
        if (parent) parent.childIds = parent.childIds.filter(function (cid) { return cid !== node.id })
      }
      if (type === 'archive') {
        node.isArchived = true
        if (!this.showArchived) detach()
      } else if (type === 'restore') {
        node.isArchived = false
      } else if (type === 'delete') {
        detach()
        node.deleted = true
      } else if (type === 'move') {
        detach()
        node.parentId = dest.id
        if (dest.loaded && dest.childIds.indexOf(node.id) === -1) dest.childIds.push(node.id)
        if (node.kind === 'folder' && dest.id !== ROOT_ID) this.$set(this.desktopExpanded, dest.id, true)
      }
      if (this.selected[node.id]) this.$delete(this.selected, node.id)
    },

    afterItems (type, results, dest) {
      var self = this
      var refresh = []
      var ok = []
      var failed = []
      results.forEach(function (x) {
        var parent = self.nodes[x.oldParentId]
        if (x.r.success) {
          ok.push(x.item)
          refresh.push(x.oldParentId)
          if (parent && parent.parentId) refresh.push(parent.parentId)
          ;(x.r.affectedFolderIds || []).forEach(function (fid) { refresh.push(fid) })
        } else {
          failed.push({ id: x.item.id, name: x.item.name, reason: x.r.failureReason })
        }
      })
      if (type === 'move' && dest) {
        refresh.push(dest.id)
        if (dest.parentId) refresh.push(dest.parentId)
        // If the current folder (or an ancestor) moved, stay in it and reveal its new location
        if (ok.some(function (n) { return pathOf(self.nodes, self.currentId).indexOf(n) !== -1 })) this.revealCurrent()
      }
      if (ok.length > 0) this.refreshAfter(refresh, null)
      this.ensureCurrentVisible()

      var verb = VERBS[type] || 'Done'
      if (results.length === 1) {
        if (ok.length === 1) {
          this.$toasted.success(type === 'move' ? 'Moved to ' + dest.name : verb + ' \'' + ok[0].name + '\'', { duration: 2500 })
        } else {
          this.actionError = failed[0].reason
          this.$toasted.error(failed[0].reason, { duration: 5000 })
        }
        return
      }
      var summary = verb + ' ' + ok.length + (failed.length ? ' · ' + failed.length + ' failed' : '')
      if (failed.length) {
        this.$toasted.error(summary, { duration: 5000 })
        this.failureReport = { title: plural(failed.length, 'item') + ' could not be ' + verb.toLowerCase(), items: failed }
      } else {
        this.$toasted.success(summary, { duration: 3000 })
      }
    },

    // If the current folder (or an ancestor) is now deleted, or archived while hidden, go to its parent.
    ensureCurrentVisible () {
      var self = this
      var chain = pathOf(this.nodes, this.currentId)
      var gone = chain.filter(function (n) {
        return n.id !== ROOT_ID && (n.deleted || (n.isArchived && !self.showArchived))
      })[0]
      if (gone) this.navigateTo(this.nodes[gone.parentId || ROOT_ID] || this.nodes[ROOT_ID])
    },

    setShowArchived (on) {
      var self = this
      if (this.actionPending) return
      this.showArchived = on === true
      writeShowArchived(this.showArchived)
      this.selected = {}
      if (!this.showArchived) this.ensureCurrentVisible()
      // Refetch everything already loaded; expansion state and nodes are kept
      Object.keys(this.nodes).forEach(function (id) {
        var n = self.nodes[id]
        if (n && n.kind === 'folder' && n.loaded) self.loadChildren(id, true)
      })
    },

    // ----- single-item entry points -----
    archiveItem (node) {
      var self = this
      if (!canArchive(node) || node.isArchived) return
      if (node.kind === 'file') {
        this.runItems('archive', [node])
        return
      }
      this.openConfirm({
        title: 'Archive folder',
        message: 'Archive \'' + node.name + '\'? It and everything in it will be hidden from everyone. You can restore it later.',
        okLabel: 'Archive',
        run: function () { return self.runItems('archive', [node]) }
      })
    },

    restoreItem (node) {
      if (!canArchive(node) || !node.isArchived) return
      this.runItems('restore', [node])
    },

    deleteItem (node) {
      var self = this
      var reason = deleteBlockReason(node)
      if (reason) {
        this.$toasted.error(reason, { duration: 4000 })
        return
      }
      this.openConfirm({
        title: node.kind === 'file' ? 'Delete file' : 'Delete folder',
        message: deleteConfirmText(node),
        okLabel: 'Delete',
        danger: true,
        run: function () { return self.runItems('delete', [node]) }
      })
    },

    // ----- confirmation -----
    openConfirm (opts) {
      this.sheet = null
      this.sheetNode = null
      this.confirmBox = {
        title: opts.title,
        message: opts.message,
        skipped: opts.skipped || [],
        okLabel: opts.okLabel || null,
        danger: opts.danger === true,
        run: opts.run || null
      }
    },

    closeConfirm () {
      if (this.actionPending) return
      this.confirmBox = null
    },

    onConfirmHide (evt) {
      if (this.actionPending) {
        evt.preventDefault()
        return
      }
      this.confirmBox = null
    },

    runConfirm () {
      var self = this
      var box = this.confirmBox
      if (!box || !box.run || this.actionPending) return
      box.run().then(function () {
        if (self.confirmBox === box) self.confirmBox = null
        if (box.bulk) self.finishBulk()
      })
    },

    // ----- multi-select -----
    toggleSelect (item) {
      if (!canSelect(item) || this.actionPending) return
      if (this.selected[item.id]) this.$delete(this.selected, item.id)
      else this.$set(this.selected, item.id, true)
    },

    selectAll (on) {
      var sel = {}
      if (on) {
        this.desktopItems.forEach(function (n) {
          if (canSelect(n)) sel[n.id] = true
        })
      }
      this.selected = sel
    },

    clearSelection () {
      if (this.actionPending) return
      this.selected = {}
    },

    enterSelectMode (item) {
      if (!this.isPhone || this.selectMode) return
      this.sheet = null
      this.selected = {}
      if (item && canSelect(item)) this.$set(this.selected, item.id, true)
      this.selectMode = true
      var q = Object.assign({}, this.$route.query || {}, { select: '1' })
      this.$router.push({ path: this.$route.path, query: q }, function () {}, function () {})
    },

    exitSelectMode () {
      if (this.actionPending) return
      this.selectMode = false
      this.selected = {}
      this.sheet = null
      if ((this.$route.query || {}).select) this.$router.back()
    },

    // After a bulk action: clear the selection and leave phone select mode
    finishBulk () {
      this.selected = {}
      if (this.selectMode) this.exitSelectMode()
    },

    openMoreSheet () {
      if (this.selectedItems.length === 0) return
      this.sheet = 'more'
    },

    openOverflowSheet () {
      this.sheetNode = null
      this.sheet = 'overflow'
    },

    bulkMove () {
      var items = this.selectedItems.filter(function (n) { return canMoveItem(n) })
      if (items.length === 0) return
      this.openMove(items, true)
    },

    bulkArchive () {
      var self = this
      var items = this.bulkArchivable.slice()
      if (items.length === 0) return
      this.openConfirm({
        title: 'Archive ' + plural(items.length, 'item'),
        message: 'Archive ' + plural(items.length, 'item') + '? They and everything in them will be hidden from everyone. You can restore them later.',
        okLabel: 'Archive',
        run: function () { return self.runItems('archive', items) }
      })
      this.confirmBox.bulk = true
    },

    bulkRestore () {
      var self = this
      var items = this.bulkRestorable.slice()
      if (items.length === 0) return
      this.sheet = null
      this.runItems('restore', items).then(function () { self.finishBulk() })
    },

    bulkDelete () {
      var self = this
      var eligible = []
      var skipped = []
      this.selectedItems.forEach(function (n) {
        var reason = deleteBlockReason(n)
        if (reason) skipped.push({ id: n.id, name: n.name, reason: reason })
        else eligible.push(n)
      })
      if (eligible.length === 0) {
        this.openConfirm({
          title: 'Nothing to delete',
          message: 'None of the selected items can be deleted.',
          skipped: skipped
        })
        return
      }
      var msg = eligible.length === 1
        ? deleteConfirmText(eligible[0])
        : 'Delete ' + plural(eligible.length, 'item') + ' and everything in them? This can\'t be undone.'
      this.openConfirm({
        title: 'Delete ' + plural(eligible.length, 'item'),
        message: msg,
        skipped: skipped,
        okLabel: 'Delete',
        danger: true,
        run: function () { return self.runItems('delete', eligible) }
      })
      this.confirmBox.bulk = true
    },

    // Desktop
    cancelDeskEdit () {
      if (this.actionPending) return
      this.deskCreating = false
      this.deskRenamingId = null
      this.actionError = ''
    },

    openDeskCreate () {
      if (!this.canCreateHere || this.actionPending) return
      this.deskRenamingId = null
      this.actionError = ''
      this.deskCreating = true
    },

    onDeskCreateSave (name) {
      var self = this
      var parent = this.currentNode
      this.createFolder(parent, name).then(function (ok) {
        if (ok && self.currentId === parent.id) {
          self.deskCreating = false
          self.actionError = ''
        }
      })
    },

    onDeskRenameSave (item, name) {
      var self = this
      var req = item.kind === 'file' ? this.renameFile(item, name) : this.renameFolder(item, name)
      req.then(function (ok) {
        if (ok) {
          self.deskRenamingId = null
          self.actionError = ''
        }
      })
    },

    onTableAction (type, item) {
      if (this.actionPending) return
      if (type === 'rename' && canRenameItem(item)) {
        this.deskCreating = false
        this.actionError = ''
        this.deskRenamingId = item.id
      } else if (type === 'new-subfolder' && canCreateIn(item)) {
        this.pendingCreateFor = item.id
        this.navigateTo(item)
      } else if (type === 'move' && canMoveItem(item)) {
        this.openMove([item])
      } else if (type === 'archive') {
        this.archiveItem(item)
      } else if (type === 'restore') {
        this.restoreItem(item)
      } else if (type === 'delete') {
        this.deleteItem(item)
      } else if (type === 'versions') {
        this.openVersions(item)
      }
    },

    // Move picker (desktop modal + phone full screen). bulk = from a selection.
    openMove (items, bulk) {
      var self = this
      var list = (items || []).filter(function (n) { return canMoveItem(n) })
      if (list.length === 0) return
      var first = list[0]
      this.sheet = null
      this.sheetNode = null
      this.actionError = ''
      this.moveSelectedId = null
      this.moveBulk = bulk === true
      var expanded = {}
      pathOf(this.nodes, first.id).forEach(function (n) {
        if (n.id !== ROOT_ID && n.id !== first.id) expanded[n.id] = true
      })
      this.moveExpanded = expanded
      this.moveItems = list
      pathOf(this.nodes, first.id).forEach(function (n) {
        if (n.id !== first.id) self.loadChildren(n.id)
      })
    },

    closeMove () {
      if (this.actionPending) return
      this.moveItems = null
      this.moveSelectedId = null
      this.actionError = ''
    },

    onMoveModalHide (evt) {
      if (this.actionPending) {
        evt.preventDefault()
        return
      }
      this.closeMove()
    },

    onMoveTreeToggle (node) {
      var open = !this.moveExpanded[node.id]
      this.$set(this.moveExpanded, node.id, open)
      if (open) this.loadChildren(node.id)
    },

    onMoveTreeSelect (node) {
      if (this.actionPending) return
      this.moveSelectedId = node.id
      this.actionError = ''
    },

    onMovePick (dest) {
      var self = this
      var items = this.moveItems
      if (!items || !dest || this.actionPending) return
      if (isMoveDestinationDisabledFor(this.nodes, items, dest)) {
        this.actionError = 'The selection cannot be moved there.'
        return
      }
      var bulk = this.moveBulk
      this.runItems('move', items, dest).then(function (results) {
        if (!results) return
        var allOk = results.every(function (x) { return x.r.success })
        // Single item: keep the picker open on failure so the reason stays visible
        if (allOk || results.length > 1) {
          self.actionError = ''
          self.closeMove()
        }
        if (bulk) self.finishBulk()
      })
    },

    // Phone sheets
    openActionSheet (item) {
      this.actionError = ''
      this.sheetNode = item
      this.sheet = 'actions'
    },

    onSheetAction (key) {
      var node = this.sheetNode
      if (!node) return
      if (key === 'rename') this.openNameSheet('rename', node)
      else if (key === 'new-subfolder') this.openNameSheet('create', node)
      else if (key === 'move') this.openMove([node])
      else if (key === 'versions') {
        this.closeSheet()
        this.openVersions(node)
      }
      else if (key === 'archive' || key === 'restore' || key === 'delete') {
        this.closeSheet()
        if (key === 'archive') this.archiveItem(node)
        else if (key === 'restore') this.restoreItem(node)
        else this.deleteItem(node)
      }
    },

    openAddSheet (keepDest) {
      if (!this.canCreateHere) return
      this.actionError = ''
      if (keepDest !== true) this.addDestId = canUploadTo(this.currentNode) ? this.currentId : null
      this.sheetNode = this.currentNode
      this.sheet = 'add'
    },

    // ---------- F4a: uploads ----------
    uploadDest (node) {
      return {
        id: node.id,
        resourceType: node.resourceType,
        propertyId: apiPropertyId(node),
        label: folderLabel(this.nodes, node.id) || node.name
      }
    },

    // entries: [{ file, relativePath }]
    startUpload (node, entries) {
      var self = this
      var reason = uploadBlockReason(node)
      if (reason) {
        this.$toasted.error(reason, { duration: 4000 })
        return
      }
      if (!entries || entries.length === 0) return
      loadAcceptedTypes(this.$store).then(function () {
        var r = enqueueFiles(self.$store, self.uploadDest(node), entries)
        var skipped = r.skippedTypes
        if (skipped.length > 0) {
          var names = skipped.slice(0, 3).join(', ') + (skipped.length > 3 ? ' and ' + (skipped.length - 3) + ' more' : '')
          self.$toasted.error('Skipped ' + plural(skipped.length, 'file') + ' with an unsupported type: ' + names, { duration: 6000 })
        } else if (r.added === 0) {
          self.$toasted.info('No files to upload', { duration: 3000 })
        }
      })
    },

    pickFiles (ref) {
      var input = this.$refs[ref]
      if (!input) return
      input.value = ''
      // Must run inside the tap/click gesture for the picker to open
      input.click()
      if (this.sheet === 'add') this.closeSheet()
    },

    onFilesPicked (e, node) {
      var input = e.target
      var entries = entriesFromFileList(input.files)
      input.value = ''
      if (!node) {
        if (entries.length > 0) this.$toasted.error('Choose a folder to upload into', { duration: 4000 })
        return
      }
      this.startUpload(node, entries)
    },

    onUploadFolderChanged (id) {
      var n = this.nodes[id]
      if (!n) return
      this.refreshAfter(n.parentId ? [id, n.parentId] : [id], null)
    },

    // Phone: choose a different destination before picking files
    openDestPicker () {
      this.sheet = null
      this.sheetNode = null
      this.destPickOpen = true
    },

    closeDestPicker () {
      this.destPickOpen = false
      this.openAddSheet(true)
    },

    onDestPick (node) {
      if (!node || !canUploadTo(node)) return
      this.addDestId = node.id
      this.closeDestPicker()
    },

    // Desktop drag and drop
    onContentDragOver (e) {
      if (this.isPhone || !isFileDrag(e)) return
      e.preventDefault()
      e.stopPropagation()
      e.dataTransfer.dropEffect = this.canUploadHere ? 'copy' : 'none'
      this.contentDragOver = true
    },

    onContentDragLeave (e) {
      if (!this.contentDragOver) return
      var to = e.relatedTarget
      if (to && e.currentTarget.contains(to)) return
      this.contentDragOver = false
    },

    onContentDrop (e) {
      if (this.isPhone || !isFileDrag(e)) return
      e.preventDefault()
      e.stopPropagation()
      this.contentDragOver = false
      this.handleDrop(this.currentNode, e)
    },

    onTreeDrop (node, e) {
      this.handleDrop(node, e)
    },

    handleDrop (node, e) {
      var self = this
      var reason = uploadBlockReason(node)
      if (reason) {
        this.$toasted.error(reason, { duration: 4000 })
        return
      }
      // Read the DataTransfer synchronously; directories are walked afterwards
      entriesFromDrop(e.dataTransfer).then(function (entries) {
        self.startUpload(node, entries)
      })
    },

    onWindowDragOver (e) {
      if (!isFileDrag(e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = 'none'
    },

    onWindowDrop (e) {
      if (!isFileDrag(e)) return
      e.preventDefault()
      this.contentDragOver = false
      if (this.$refs.deskTree) this.$refs.deskTree.clearDropTarget()
    },

    openNameSheet (mode, node) {
      var self = this
      if (mode === 'rename' && !canRenameItem(node)) return
      if (mode === 'create' && !canCreateIn(node)) return
      this.actionError = ''
      this.nameMode = mode
      this.nameValue = mode === 'rename' ? node.name : ''
      this.sheetNode = node
      this.sheet = 'name'
      this.$nextTick(function () {
        if (self.$refs.nameInput) self.$refs.nameInput.focus()
      })
    },

    saveNameSheet () {
      var self = this
      if (this.actionPending || !this.sheetNode) return
      var req = this.nameMode === 'rename'
        ? (this.sheetNode.kind === 'file' ? this.renameFile(this.sheetNode, this.nameValue) : this.renameFolder(this.sheetNode, this.nameValue))
        : this.createFolder(this.sheetNode, this.nameValue)
      req.then(function (ok) {
        if (ok) self.closeSheet()
      })
    },

    closeSheet () {
      if (this.actionPending) return
      this.sheet = null
      this.sheetNode = null
      this.actionError = ''
    },

    // Same behaviour as the classic view: documents download, images open the gallery, videos play.
    openItem (item) {
      var self = this
      if (item.kind === 'folder') {
        this.navigateTo(item)
        return
      }
      var raw = item.raw
      if (item.mediaType === 'Image') {
        this.galleryIndex = this.currentImages.indexOf(item)
      } else if (item.mediaType === 'Video') {
        this.activeVideoIndex = Math.max(0, this.currentVideos.indexOf(item))
        this.videoPlayerVisible = false
        this.$nextTick(function () {
          self.videoPlayerVisible = true
        })
      } else {
        this.$store.dispatch('resources/downloadDocument', {
          documentId: raw.id,
          propertyId: raw.propertyId === undefined ? null : raw.propertyId,
          fileName: raw.name,
          eventId: raw.eventId
        })
      }
    }
  }
}
</script>

<style scoped>
.rx {
  color: #1f2937;
}
.rx--phone {
  margin: 0;
}
.rx-desk {
  padding: 16px 24px 24px;
}
.rx-desk__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.rx-desk__title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 24px 0 0;
}
.rx-desk__search {
  position: relative;
  flex: 1 1 280px;
  max-width: 480px;
  margin-right: auto;
}
.rx-desk__search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
}
.rx-desk__input {
  width: 100%;
  height: 38px;
  padding: 0 12px 0 36px;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
}
.rx-desk__input:focus {
  border-color: #1a7f86;
}
.rx-desk__actions {
  display: flex;
  margin-left: 16px;
}
.rx-btn {
  height: 38px;
  padding: 0 14px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  margin-left: 8px;
  white-space: nowrap;
}
.rx-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.rx-btn--outline {
  background: #fff;
  border: 1px solid #1e7e34;
  color: #1e7e34;
}
.rx-btn--solid {
  background: #1e7e34;
  border: 1px solid #1e7e34;
  color: #fff;
}
.rx-btn--outline-plain {
  background: #fff;
  border: 1px solid #e2e5ea;
  color: #1f2937;
}
.rx-desk__main {
  display: flex;
  align-items: flex-start;
}
.rx-card {
  background: #fff;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
}
.rx-desk__tree {
  flex: 0 0 300px;
  width: 300px;
  margin-right: 16px;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
}
.rx-desk__tree-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid #e2e5ea;
}
.rx-desk__tree-body {
  padding: 6px;
  overflow-y: auto;
}
.rx-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.rx-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #1a7f86;
  font-size: 14px;
}
.rx-link:hover {
  text-decoration: underline;
}
.rx-desk__content {
  flex: 1 1 auto;
  min-width: 0;
}
.rx-desk__content-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-bottom: 1px solid #e2e5ea;
}
.rx-crumbs {
  min-width: 0;
  font-size: 15px;
  margin-right: 12px;
}
.rx-crumbs__link {
  color: #1a7f86;
}
.rx-crumbs__sep {
  color: #6b7280;
  margin: 0 6px;
}
.rx-crumbs__current {
  color: #1f2937;
}
.rx-desk__content-actions {
  display: flex;
  align-items: center;
}
.rx-move__intro {
  font-size: 14px;
  color: #4b5563;
}
.rx-move__tree {
  max-height: 55vh;
  overflow-y: auto;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  padding: 6px;
}
.rx-move__error,
.rx-nameform__error {
  margin-top: 8px;
  font-size: 14px;
  color: #b91c1c;
}
.rx-move__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.rx-move__footer .rx-btn {
  height: 44px;
}
.rx-fab {
  position: fixed;
  right: calc(16px + env(safe-area-inset-right));
  bottom: calc(16px + env(safe-area-inset-bottom));
  z-index: 1030;
  width: 56px;
  height: 56px;
  border: 0;
  border-radius: 50%;
  background: #1e7e34;
  color: #fff;
  font-size: 22px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
}
.rx-fab--lifted {
  bottom: calc(72px + env(safe-area-inset-bottom));
}
.rx-tray-spacer {
  height: 64px;
}
.rx-hidden-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}
.rx-desk__tree-foot {
  padding: 10px 14px;
  border-top: 1px solid #e2e5ea;
  font-size: 13px;
  color: #6b7280;
}
.rx-desk__content {
  position: relative;
}
.rx-desk__content.is-drop-target {
  outline: 2px dashed #1a7f86;
  outline-offset: -2px;
}
.rx-drop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px 24px 24px;
  background: rgba(227, 241, 239, 0.92);
  border-radius: 8px;
  pointer-events: none;
}
.rx-drop--blocked {
  background: rgba(243, 244, 246, 0.92);
}
.rx-drop__box {
  max-width: 420px;
  text-align: center;
  color: #1f2937;
}
.rx-drop__icon {
  font-size: 36px;
  color: #1a7f86;
  margin-bottom: 12px;
}
.rx-drop--blocked .rx-drop__icon {
  color: #9ca3af;
}
.rx-drop__title {
  font-size: 18px;
  font-weight: 700;
}
.rx-drop__sub {
  margin-top: 6px;
  font-size: 14px;
  color: #4b5563;
}
.rx-adddest {
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 4px 8px 4px 20px;
  background: #f7f8fa;
  border-bottom: 1px solid #eef0f2;
  font-size: 14px;
  color: #4b5563;
}
.rx-adddest__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-adddest__change {
  flex: 0 0 auto;
  min-width: 44px;
  min-height: 44px;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: #1a7f86;
  font-size: 16px;
  font-weight: 600;
}
.rx-srows {
  display: flex;
  flex-direction: column;
}
.rx-srow {
  display: flex;
  align-items: center;
  min-height: 56px;
  width: 100%;
  padding: 8px 20px;
  border: 0;
  border-bottom: 1px solid #eef0f2;
  background: #fff;
  color: #1f2937;
  font-size: 16px;
  text-align: left;
}
.rx-srow:active {
  background: #f3f4f6;
}
.rx-srow:disabled {
  color: #9ca3af;
}
.rx-srow:disabled:active {
  background: #fff;
}
.rx-srow__icon {
  flex: 0 0 24px;
  width: 24px;
  margin-right: 16px;
  text-align: center;
  color: #4b5563;
}
.rx-srow:disabled .rx-srow__icon {
  color: #c4c9d0;
}
.rx-srow__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.rx-srow__sub {
  font-size: 13px;
  color: #6b7280;
}
.rx-srow:disabled .rx-srow__sub {
  color: #9ca3af;
}
.rx-srow--cancel {
  justify-content: center;
  font-weight: 600;
  color: #1a7f86;
  border-bottom: 0;
}
.rx-nameform {
  padding: 16px 20px 20px;
}
.rx-nameform__input {
  width: 100%;
  height: 48px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 16px;
  color: #1f2937;
  outline: none;
}
.rx-nameform__input:focus {
  border-color: #1a7f86;
}
.rx-nameform__buttons {
  display: flex;
  margin-top: 16px;
}
.rx-nameform__btn {
  flex: 1 1 0;
  min-height: 48px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #fff;
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
}
.rx-nameform__btn + .rx-nameform__btn {
  margin-left: 12px;
}
.rx-nameform__btn--primary {
  background: #1e7e34;
  border-color: #1e7e34;
  color: #fff;
}
.rx-nameform__btn:disabled {
  opacity: 0.5;
}
.rx-check {
  margin: 0 4px 0 0;
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
}
.rx-check input {
  margin-right: 4px;
  vertical-align: middle;
}
.rx-btn--danger {
  background: #b91c1c;
  border: 1px solid #b91c1c;
  color: #fff;
}
.rx-bulk {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 6px 14px;
  background: #e3f1ef;
  border-bottom: 1px solid #cfe5e1;
}
.rx-bulk__count {
  font-weight: 700;
  font-size: 14px;
  margin-right: 12px;
}
.rx-bulk__btn {
  height: 36px;
  padding: 0 12px;
  margin: 2px 4px 2px 0;
  border: 1px solid #b9d9d4;
  border-radius: 6px;
  background: #fff;
  color: #1f2937;
  font-size: 14px;
  white-space: nowrap;
}
.rx-bulk__btn i {
  margin-right: 4px;
}
.rx-bulk__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.rx-bulk__btn--danger {
  color: #b91c1c;
}
.rx-bulk__clear {
  margin-left: auto;
  border-color: transparent;
  background: transparent;
  color: #1a7f86;
  font-weight: 600;
}
.rx-selbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1030;
  display: flex;
  height: calc(64px + env(safe-area-inset-bottom));
  padding-bottom: env(safe-area-inset-bottom);
  background: #fff;
  border-top: 1px solid #e2e5ea;
}
.rx-selbar__btn {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  border: 0;
  background: transparent;
  color: #1f2937;
  font-size: 13px;
}
.rx-selbar__btn i {
  font-size: 18px;
  margin-bottom: 4px;
  color: #1a7f86;
}
.rx-selbar__btn--tight span {
  font-size: 12px;
  white-space: nowrap;
}
.rx-selbar__btn:disabled {
  color: #9ca3af;
}
.rx-selbar__btn:disabled i {
  color: #c4c9d0;
}
.rx-srow--danger,
.rx-srow--danger .rx-srow__icon {
  color: #b91c1c;
}
.rx-srow__text--grow {
  flex: 1 1 auto;
}
.rx-switch {
  flex: 0 0 auto;
  position: relative;
  width: 44px;
  height: 26px;
  border-radius: 13px;
  background: #d1d5db;
  transition: background 0.15s;
}
.rx-switch.is-on {
  background: #1a7f86;
}
.rx-switch__knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.15s;
}
.rx-switch.is-on .rx-switch__knob {
  left: 21px;
}
.rx-confirm__msg {
  font-size: 15px;
  margin: 0 0 12px;
}
.rx-confirm--phone {
  padding: 16px 20px 20px;
}
.rx-confirm__label {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
}
.rx-confirm__list {
  max-height: 40vh;
  overflow-y: auto;
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
}
.rx-confirm__reason {
  color: #6b7280;
}
.rx-nameform__btn--danger {
  background: #b91c1c;
  border-color: #b91c1c;
  color: #fff;
}
</style>
